import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  #accountStyleTask = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.addEventListener('click', this.#onAccountInteraction);
    this.#syncCartTriggerAriaExpanded();
    this.#ensureAccountStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.removeEventListener('click', this.#onAccountInteraction);
  }

  #onAccountInteraction = (event) => {
    if (event.composedPath().some((element) => element instanceof HTMLElement && element.localName === 'shopify-account')) {
      this.#ensureAccountStyles();
    }
  };

  #ensureAccountStyles() {
    if (this.#accountStyleTask) return;

    this.#accountStyleTask = customElements.whenDefined('shopify-account').then(
      () =>
        new Promise((resolve) => {
          let attempts = 0;

          const inject = () => {
            if (!this.isConnected) {
              resolve();
              return;
            }

            const account = this.querySelector('shopify-account');
            const root = account?.shadowRoot;
            if (root) {
              if (!root.getElementById('aoctant-account-outline-actions-style')) {
                const style = document.createElement('style');
                style.id = 'aoctant-account-outline-actions-style';
                style.textContent = `
                  .button.outline,
                  .button.outline:hover,
                  .button.outline:focus-visible {
                    background: #262323 !important;
                    color: #fff !important;
                    border-color: #262323 !important;
                    border-radius: 6px !important;
                    box-shadow: none !important;
                  }
                `;
                root.append(style);
              }
              resolve();
              return;
            }

            attempts += 1;
            if (attempts < 12) requestAnimationFrame(inject);
            else resolve();
          };

          inject();
        })
    ).finally(() => {
      this.#accountStyleTask = null;
    });
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
